import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Layout, Modal } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	isAllowedToPerform,
	isBuyerAllowedToAccess,
	PERMISSION_NAMES,
} from '../../utils/AuthUtils';
import { EmptyState } from '../empty_state/EmptyState';
import PermissionDeniedPage from '../permission_denied_page/PermissionDeniedPage';
import OWButton from '../ow_button/OWButton';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import { ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS } from '../../actions/entity_add_requests_actions';
import {
	entityAddRequestsRestCrudThunksForBuyer,
	refreshEntityAddRequestCounts,
} from '../../thunks/entity_add_requests_thunks';
import {
	ENTITY_REQUEST_ENTITY_TYPES,
	REQUEST_TO_ADD_ENTITIES_FEATURE_FLAG,
} from '../../types/entity_add_requests';
import posthog from 'posthog-js';
import {
	REQUESTER_PIPELINE_FILTERS,
	APPROVER_PIPELINE_FILTERS,
} from './entity_add_request_pipeline_filters';
import { getEntityAddRequestColumns } from './entityAddRequestColumns';
import RequestToAddSupplierForm from '../request_to_add_supplier_form/RequestToAddSupplierForm';
import ReviewEntityAddRequestModal from '../review_entity_add_request_modal/ReviewEntityAddRequestModal';
import ViewEntityAddRequestModal from '../review_entity_add_request_modal/ViewEntityAddRequestModal';
import EntityAddRequestAuditsModal from '../entity_add_request_audits_modal/EntityAddRequestAuditsModal';
import EntityRequestFormBuilder from '../entity_request_form/EntityRequestFormBuilder';

const { Content } = Layout;
const TARGET_COLLECTION = 'entityAddRequestsIndex';

function RequestsToAddSuppliersIndexPage({
	entityAddRequests,
	currentUser,
	companyConfig,
	updateFilters,
	clearAndUpdateFilters,
	updateSorters,
	refreshCounts,
	deleteRequest,
}) {
	const [refreshToken, setRefreshToken] = useState(0);
	const [requestFormRecord, setRequestFormRecord] = useState(undefined);
	const [requestFormVisible, setRequestFormVisible] = useState(false);
	const [reviewRecord, setReviewRecord] = useState(undefined);
	const [viewRecord, setViewRecord] = useState(undefined);
	const [auditRecord, setAuditRecord] = useState(undefined);
	const [formBuilderVisible, setFormBuilderVisible] = useState(false);

	const canApprove = useMemo(
		() =>
			isAllowedToPerform(
				PERMISSION_NAMES.CAN_APPROVE_ENTITY_REQUESTS,
				companyConfig,
				nullSafeGetOrElse('roles', currentUser, [])
			),
		[companyConfig, currentUser]
	);

	// Submitting a new-supplier request is gated by the requestNewSuppliers access permission
	// (nested under accessPermissions.others — use the nested reader, not isAllowedToPerform).
	const canRequest = useMemo(
		() =>
			isBuyerAllowedToAccess(
				PERMISSION_NAMES.REQUEST_NEW_SUPPLIERS,
				companyConfig,
				nullSafeGetOrElse('roles', currentUser, [])
			),
		[companyConfig, currentUser]
	);

	const refreshTable = () => setRefreshToken((token) => token + 1);

	const openNewRequest = () => {
		setRequestFormRecord(undefined);
		setRequestFormVisible(true);
	};
	const openEdit = (record) => {
		setRequestFormRecord(record);
		setRequestFormVisible(true);
	};
	const hideRequestForm = () => setRequestFormVisible(false);

	const openReview = (record) => setReviewRecord(record);
	const hideReview = () => setReviewRecord(undefined);

	const openView = (record) => setViewRecord(record);
	const hideView = () => setViewRecord(undefined);

	const openAudits = (record) => setAuditRecord(record);
	const hideAudits = () => setAuditRecord(undefined);

	const openFormBuilder = () => setFormBuilderVisible(true);
	const hideFormBuilder = () => setFormBuilderVisible(false);

	const handleView = (record) => openView(record);

	const handleDelete = (record) => {
		Modal.confirm({
			title: 'Delete this request?',
			content: 'This request will be permanently removed.',
			okText: 'Delete',
			okType: 'danger',
			onOk: () => deleteRequest(record).then(refreshTable),
		});
	};

	const handleActionSuccess = () => {
		refreshTable();
	};

	const currentUserEmail = nullSafeGet('email', currentUser);

	const columns = useMemo(
		() =>
			getEntityAddRequestColumns({
				canApprove,
				currentUserEmail,
				onReview: openReview,
				onEdit: openEdit,
				onDelete: handleDelete,
				onView: handleView,
				onShowAudits: openAudits,
			}),
		[canApprove, currentUserEmail]
	);

	// The list is company-wide for everyone; ownership only gates the edit/delete row actions.
	const preAppliedFilters = useMemo(
		() => ({ entityType: ENTITY_REQUEST_ENTITY_TYPES.supplier }),
		[]
	);

	const initialFilters = {};

	const rightActions = canApprove ? (
		<OWButton icon={<EditOutlined />} onClick={openFormBuilder}>
			Edit Request Form
		</OWButton>
	) : canRequest ? (
		<OWButton type="primary" size="large" onClick={openNewRequest}>
			+ New Request
		</OWButton>
	) : null;

	const emptyState = (
		<EmptyState
			graphic={
				<img
					alt=""
					style={{ marginBottom: 8 }}
					src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
				/>
			}
			headline={'No requests to add yet.'}
			body={
				<div style={{ textAlign: 'center', maxWidth: 440 }}>
					{canApprove
						? 'There are no supplier requests to review right now.'
						: 'Request a new supplier to get started.'}
				</div>
			}
		/>
	);

	// The route only checks authorizedRoles, so gate the list itself. Buyer admins auto-pass the
	// permission checks, so also require the PostHog feature flag — otherwise direct navigation would
	// expose the company-wide table + form builder while the feature is disabled.
	const featureEnabled = !!posthog.isFeatureEnabled(REQUEST_TO_ADD_ENTITIES_FEATURE_FLAG);
	if (!featureEnabled || (!canApprove && !canRequest)) {
		return <PermissionDeniedPage />;
	}

	return (
		<Content className="requestsToAddPage" style={{ padding: '0 0.5em' }}>
			{requestFormVisible ? (
				<RequestToAddSupplierForm
					visible={requestFormVisible}
					record={requestFormRecord}
					hideModal={hideRequestForm}
					onSuccess={handleActionSuccess}
				/>
			) : null}
			{reviewRecord ? (
				<ReviewEntityAddRequestModal
					visible={!!reviewRecord}
					record={reviewRecord}
					hideModal={hideReview}
					onSuccess={handleActionSuccess}
				/>
			) : null}
			{viewRecord ? (
				<ViewEntityAddRequestModal
					visible={!!viewRecord}
					record={viewRecord}
					hideModal={hideView}
				/>
			) : null}
			{auditRecord ? (
				<EntityAddRequestAuditsModal
					visible={!!auditRecord}
					record={auditRecord}
					hideModal={hideAudits}
				/>
			) : null}
			{formBuilderVisible ? (
				<EntityRequestFormBuilder
					visible={formBuilderVisible}
					hideModal={hideFormBuilder}
					onSuccess={handleActionSuccess}
				/>
			) : null}
			<PaginatedReduxTableWithHeader
				key={refreshToken}
				targetCollectionName={TARGET_COLLECTION}
				showHeader
				updateFilters={updateFilters}
				clearAndUpdateFilters={clearAndUpdateFilters}
				updateSorters={updateSorters}
				refreshCounts={refreshCounts}
				stateSlice={entityAddRequests}
				pipeLineFilters={canApprove ? APPROVER_PIPELINE_FILTERS : REQUESTER_PIPELINE_FILTERS}
				filterConfig={[]}
				entityCollectionName="entity_add_requests"
				tableColumns={columns}
				fetchData={entityAddRequestsRestCrudThunksForBuyer.read}
				initialPagination={{ current: 1, pageSize: 10 }}
				initialSorters={{ sort_by: 'id', order: 'descend' }}
				initialFilters={initialFilters}
				preAppliedFilters={preAppliedFilters}
				rightActions={rightActions}
				emptyState={emptyState}
			/>
		</Content>
	);
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	entityAddRequests: state.entity_add_requests,
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollection) =>
		dispatch(
			ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollection)
		),
	updateSorters: (sortBy, order, targetCollection) =>
		dispatch(
			ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS.updateSorting(sortBy, order, targetCollection)
		),
	refreshCounts: (filters) => dispatch(refreshEntityAddRequestCounts(filters)),
	deleteRequest: (record) => dispatch(entityAddRequestsRestCrudThunksForBuyer.delete(record)),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(RequestsToAddSuppliersIndexPage)
);
